import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import apiService from '../services/api';
import { Feedback } from '../types';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: any;
  }
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportGenerationPage: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [generatedReports, setGeneratedReports] = useState<any[]>([]);
  const [feedbackData, setFeedbackData] = useState<Feedback[]>([]);

  const [projectCodeSearch, setProjectCodeSearch] = useState('');

  const [projectFormData, setProjectFormData] = useState({
    projectId: '',
    reportType: 'summary',
    startDate: '',
    endDate: '',
    format: 'pdf',
  });

  const [feedbackFormData, setFeedbackFormData] = useState({
    reportType: 'summary',
    startDate: '',
    endDate: '',
    format: 'pdf',
  });

  const fetchProjects = React.useCallback(async () => {
    try {
      const response = await apiService.getProjects(1, 100);
      setProjects(response.data || []);
    } catch (err) {
      setError('Failed to fetch projects');
    }
  }, []);

  const fetchAllProjects = React.useCallback(async () => {
    const allProjects: any[] = [];
    const limit = 200;
    let page = 1;
    let totalPages = 1;

    do {
      const response = await apiService.getProjects(page, limit);
      allProjects.push(...(response.data || []));
      totalPages = response.pagination?.pages || 1;
      page += 1;
    } while (page <= totalPages);

    return allProjects;
  }, []);

  const fetchFeedback = React.useCallback(async () => {
    try {
      const response = await apiService.get('/feedback');
      setFeedbackData(response.data || []);
    } catch (err) {
      setError('Failed to fetch feedback');
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchFeedback();
  }, [fetchProjects, fetchFeedback]);

  const handleProjectFormChange = (e: any) => {
    const { name, value } = e.target;
    setProjectFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFeedbackFormChange = (e: any) => {
    const { name, value } = e.target;
    setFeedbackFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchByProjectCode = async () => {
    setError('');

    const code = projectCodeSearch.trim();
    if (!code) {
      setError('Please enter a project serial number or name to search');
      return;
    }

    try {
      const response = await apiService.getProjectByCode(code);
      const project = response.data;

      if (!project) {
        setError('Project not found for the given search value');
        return;
      }

      setProjects(prev => {
        if (prev.some((p: any) => p.id === project.id)) {
          return prev;
        }
        return [project, ...prev];
      });

      setProjectFormData(prev => ({
        ...prev,
        projectId: project.id,
      }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to find project');
    }
  };

  const exportToCSV = (data: any[], fileName: string) => {
    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row =>
        Object.values(row)
          .map(val => `"${val}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToExcel = (data: any[], fileName: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const loadLogoDataUrl = async () => {
    try {
      const response = await fetch('/logo192.png');
      if (!response.ok) return null;
      const blob = await response.blob();

      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read logo file'));
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const exportToPDF = async (data: any[], fileName: string, title: string) => {
    if (!data.length) {
      throw new Error('No data available for PDF export');
    }

    const doc = new jsPDF();
    const logo = await loadLogoDataUrl();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const leftMargin = 14;
    const labelX = leftMargin;
    const valueX = 72;
    const valueMaxWidth = pageWidth - valueX - leftMargin;
    let y = 18;

    const drawHeader = () => {
      if (logo) {
        doc.addImage(logo, 'PNG', leftMargin, 10, 14, 14);
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, logo ? leftMargin + 18 : leftMargin, 17);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleString()}`, leftMargin, 24);

      y = 30;
      doc.setDrawColor(200);
      doc.line(leftMargin, y, pageWidth - leftMargin, y);
      y += 6;
    };

    const ensurePageSpace = (requiredHeight: number) => {
      if (y + requiredHeight > pageHeight - 14) {
        doc.addPage();
        drawHeader();
      }
    };

    drawHeader();

    try {
      data.forEach((item, index) => {
        const entries = Object.entries(item);
        const estimatedHeight = (entries.length * 9) + 14;
        ensurePageSpace(estimatedHeight);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(`Record ${index + 1}`, leftMargin, y);
        y += 6;

        entries.forEach(([key, value]) => {
          const printable = value == null ? 'N/A' : String(value);
          const wrapped = doc.splitTextToSize(printable, valueMaxWidth);

          ensurePageSpace((wrapped.length * 5) + 6);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.text(`${key}:`, labelX, y);

          doc.setFont('helvetica', 'normal');
          doc.text(wrapped, valueX, y);

          y += Math.max(6, wrapped.length * 5 + 1);
        });

        doc.setDrawColor(220);
        doc.line(leftMargin, y, pageWidth - leftMargin, y);
        y += 8;
      });

      const pageCount = doc.getNumberOfPages();
      for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Page ${page} of ${pageCount}`, pageWidth - leftMargin, pageHeight - 8, { align: 'right' } as any);
      }

      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('PDF generation error:', error);
      throw new Error('Failed to generate PDF. Please try another format.');
    }
  };

  const handleGenerateProjectReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!projectFormData.projectId) {
      setError('Please select a project');
      return;
    }

    setIsLoading(true);

    try {
      let selectedProjects: any[] = [];

      if (projectFormData.projectId === '__ALL__') {
        selectedProjects = await fetchAllProjects();
      } else {
        const selectedProject = projects.find((p) => p.id === projectFormData.projectId);
        if (!selectedProject) {
          setError('Selected project not found');
          setIsLoading(false);
          return;
        }
        selectedProjects = [selectedProject];
      }

      if (projectFormData.startDate) {
        const start = new Date(projectFormData.startDate);
        selectedProjects = selectedProjects.filter((p) => p.createdAt && new Date(p.createdAt) >= start);
      }

      if (projectFormData.endDate) {
        const end = new Date(projectFormData.endDate);
        selectedProjects = selectedProjects.filter((p) => p.createdAt && new Date(p.createdAt) <= end);
      }

      if (selectedProjects.length === 0) {
        setError('No projects found for the selected filters');
        setIsLoading(false);
        return;
      }

      const reportData = selectedProjects.map((project) => ({
        'S/No': project.serialNo ?? 'N/A',
        'Project Name': project.projectName || project.title,
        'Location (Ward)': project.ward || 'N/A',
        'Brief Project Description': project.briefProjectDescription || project.description || 'N/A',
        'Contract Sum': project.contractSum ?? project.budget ?? 'N/A',
        'Timeframe Start-Finish': project.timeframeStartFinish || project.timeframe || 'N/A',
        'Timeframe Start Date': project.timeframeStartDate || 'N/A',
        'Timeframe End Date': project.timeframeEndDate || 'N/A',
        'Project Implementation Status': project.implementationStatus || project.status || 'N/A',
        'Created': project.createdAt ? new Date(project.createdAt).toLocaleDateString() : 'N/A',
      }));

      const isAllProjects = projectFormData.projectId === '__ALL__';
      const fileName = isAllProjects ? `project-report-all-${Date.now()}` : `project-report-${selectedProjects[0].id}`;
      const reportTitle = isAllProjects
        ? `All Projects - ${projectFormData.reportType.charAt(0).toUpperCase() + projectFormData.reportType.slice(1)} Report`
        : `${selectedProjects[0].title} - ${projectFormData.reportType.charAt(0).toUpperCase() + projectFormData.reportType.slice(1)} Report`;

      if (projectFormData.format === 'csv') {
        exportToCSV(reportData, fileName);
      } else if (projectFormData.format === 'excel') {
        exportToExcel(reportData, fileName);
      } else if (projectFormData.format === 'pdf') {
        await exportToPDF(reportData, fileName, reportTitle);
      }

      setGeneratedReports([
        {
          id: Date.now(),
          name: `${isAllProjects ? 'All Projects' : selectedProjects[0].title} - ${projectFormData.reportType}`,
          type: 'project',
          format: projectFormData.format,
          generatedAt: new Date().toLocaleString(),
        },
        ...generatedReports,
      ]);

      setSuccessMessage(`Report exported successfully as ${projectFormData.format.toUpperCase()}!`);
      setOpenSnackbar(true);
      setProjectFormData({
        projectId: '',
        reportType: 'summary',
        startDate: '',
        endDate: '',
        format: 'pdf',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateFeedbackReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    setIsLoading(true);

    try {
      // Filter feedback by date range if specified
      let filteredFeedback = feedbackData;
      
      if (feedbackFormData.startDate) {
        const startDate = new Date(feedbackFormData.startDate);
        filteredFeedback = filteredFeedback.filter(f => new Date(f.createdAt) >= startDate);
      }
      
      if (feedbackFormData.endDate) {
        const endDate = new Date(feedbackFormData.endDate);
        filteredFeedback = filteredFeedback.filter(f => new Date(f.createdAt) <= endDate);
      }

      // Fetch project details for each feedback
      const feedbackWithProjects = await Promise.all(
        filteredFeedback.map(async (fb) => {
          let projectInfo = { name: 'Unknown Project', code: 'N/A' };
          if (fb.projectId) {
            try {
              const projectResponse = await apiService.getProjectById(fb.projectId);
              if (projectResponse.success && projectResponse.data) {
                projectInfo = {
                  name: projectResponse.data.title,
                  code: projectResponse.data.serialNo ? String(projectResponse.data.serialNo) : 'N/A'
                };
              }
            } catch (err) {
              console.warn(`Failed to fetch project for feedback ${fb.id}:`, err);
            }
          }
          return { ...fb, projectInfo };
        })
      );

      const reportData = feedbackWithProjects.map(fb => ({
        'Project Name': fb.projectInfo.name,
        'Project Serial No': fb.projectInfo.code,
        'Type': fb.feedbackType,
        'Message': fb.message,
        'Status': fb.status,
        'Admin Response': fb.response || 'No response',
        'Responded Date': fb.response && fb.updatedAt ? new Date(fb.updatedAt).toLocaleDateString() : 'Pending',
        'Further Feedback': fb.userResponse || 'None',
        'Created': new Date(fb.createdAt).toLocaleDateString(),
      }));

      if (reportData.length === 0) {
        setError('No feedback found for the selected date range');
        setIsLoading(false);
        return;
      }

      const fileName = `feedback-report-${new Date().getTime()}`;
      const reportTitle = `Feedback Report - ${feedbackFormData.reportType.charAt(0).toUpperCase() + feedbackFormData.reportType.slice(1)}`;

      if (feedbackFormData.format === 'csv') {
        exportToCSV(reportData, fileName);
      } else if (feedbackFormData.format === 'excel') {
        exportToExcel(reportData, fileName);
      } else if (feedbackFormData.format === 'pdf') {
        await exportToPDF(reportData, fileName, reportTitle);
      }

      setGeneratedReports([
        {
          id: Date.now(),
          name: `Feedback Report - ${feedbackFormData.reportType}`,
          type: 'feedback',
          format: feedbackFormData.format,
          generatedAt: new Date().toLocaleString(),
        },
        ...generatedReports,
      ]);

      setSuccessMessage(`Feedback report exported successfully as ${feedbackFormData.format.toUpperCase()}!`);
      setOpenSnackbar(true);
      setFeedbackFormData({
        reportType: 'summary',
        startDate: '',
        endDate: '',
        format: 'pdf',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to generate feedback report');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Generate Reports
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Project Reports" id="report-tab-0" aria-controls="report-tabpanel-0" />
          <Tab label="Feedback Reports" id="report-tab-1" aria-controls="report-tabpanel-1" />
          <Tab label="Generated Reports" id="report-tab-2" aria-controls="report-tabpanel-2" />
        </Tabs>

        {/* Project Reports Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ padding: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Project Report Options
                </Typography>

                <form onSubmit={handleGenerateProjectReport}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          label="Search by Serial No or Project Name"
                          value={projectCodeSearch}
                          onChange={(e) => setProjectCodeSearch(e.target.value)}
                          disabled={isLoading}
                        />
                        <Button
                          variant="outlined"
                          onClick={handleSearchByProjectCode}
                          disabled={isLoading}
                        >
                          Search
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Select Project</InputLabel>
                        <Select
                          name="projectId"
                          value={projectFormData.projectId}
                          onChange={handleProjectFormChange}
                          label="Select Project"
                          disabled={isLoading}
                        >
                          <MenuItem value="__ALL__">All Projects</MenuItem>
                          {projects.map(project => (
                            <MenuItem key={project.id} value={project.id}>
                              {project.serialNo ? `${project.serialNo} - ${project.title}` : project.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Report Type</InputLabel>
                        <Select
                          name="reportType"
                          value={projectFormData.reportType}
                          onChange={handleProjectFormChange}
                          label="Report Type"
                          disabled={isLoading}
                        >
                          <MenuItem value="summary">Summary</MenuItem>
                          <MenuItem value="detailed">Detailed</MenuItem>
                          <MenuItem value="financial">Financial</MenuItem>
                          <MenuItem value="progress">Progress</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        name="startDate"
                        type="date"
                        value={projectFormData.startDate}
                        onChange={handleProjectFormChange}
                        disabled={isLoading}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        name="endDate"
                        type="date"
                        value={projectFormData.endDate}
                        onChange={handleProjectFormChange}
                        disabled={isLoading}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Format</InputLabel>
                        <Select
                          name="format"
                          value={projectFormData.format}
                          onChange={handleProjectFormChange}
                          label="Format"
                          disabled={isLoading}
                        >
                          <MenuItem value="pdf">PDF</MenuItem>
                          <MenuItem value="excel">Excel</MenuItem>
                          <MenuItem value="csv">CSV</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? <CircularProgress size={24} /> : 'Generate & Export Report'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ padding: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Report Information
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Select a project and choose your desired report type and export format.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Report Types:</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div" sx={{ pl: 2 }}>
                  • <strong>Summary:</strong> Overview of project status and key metrics
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div" sx={{ pl: 2 }}>
                  • <strong>Detailed:</strong> Comprehensive project information
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div" sx={{ pl: 2 }}>
                  • <strong>Financial:</strong> Budget and financial metrics
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div" sx={{ pl: 2 }}>
                  • <strong>Progress:</strong> Project completion and timeline data
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Feedback Reports Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ padding: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Feedback Report Options
                </Typography>

                <form onSubmit={handleGenerateFeedbackReport}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Report Type</InputLabel>
                        <Select
                          name="reportType"
                          value={feedbackFormData.reportType}
                          onChange={handleFeedbackFormChange}
                          label="Report Type"
                          disabled={isLoading}
                        >
                          <MenuItem value="summary">Summary</MenuItem>
                          <MenuItem value="detailed">Detailed</MenuItem>
                          <MenuItem value="analysis">Analysis</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        name="startDate"
                        type="date"
                        value={feedbackFormData.startDate}
                        onChange={handleFeedbackFormChange}
                        disabled={isLoading}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        name="endDate"
                        type="date"
                        value={feedbackFormData.endDate}
                        onChange={handleFeedbackFormChange}
                        disabled={isLoading}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Format</InputLabel>
                        <Select
                          name="format"
                          value={feedbackFormData.format}
                          onChange={handleFeedbackFormChange}
                          label="Format"
                          disabled={isLoading}
                        >
                          <MenuItem value="pdf">PDF</MenuItem>
                          <MenuItem value="excel">Excel</MenuItem>
                          <MenuItem value="csv">CSV</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? <CircularProgress size={24} /> : 'Generate & Export Report'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ padding: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Feedback Statistics
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Total Feedbacks: <strong>{feedbackData.length}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  By Type:
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div" sx={{ pl: 2 }}>
                  • Suggestions: {feedbackData.filter(f => f.feedbackType === 'suggestion').length}
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div" sx={{ pl: 2 }}>
                  • Complaints: {feedbackData.filter(f => f.feedbackType === 'complaint').length}
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div" sx={{ pl: 2 }}>
                  • Appreciation: {feedbackData.filter(f => f.feedbackType === 'appreciation').length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Generated Reports Tab */}
        <TabPanel value={tabValue} index={2}>
          <Paper elevation={3} sx={{ padding: 4 }}>
            <Typography variant="h6" gutterBottom>
              Generated Reports
            </Typography>

            {generatedReports.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No reports generated yet. Generate your first report to get started.
              </Typography>
            ) : (
              <List>
                {generatedReports.map(report => (
                  <ListItem key={report.id} divider>
                    <ListItemText
                      primary={report.name}
                      secondary={`${report.format.toUpperCase()} • Generated: ${report.generatedAt}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </TabPanel>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Container>
  );
};

export default ReportGenerationPage;
